import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FigIcon } from '../components/FigIcon';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, 
  ArrowRight,
  AlertTriangle, 
  TrendingUp, 
  Clock, 
  Heart,
  Brain,
  Zap,
  Target,
  CheckCircle
} from 'lucide-react';

const WhyMetabolicHealthPage = () => {
  const navigate = useNavigate();

  const silentSignals = [
    {
      signal: "Your waist has grown 2+ inches in the last 5 years",
      meaning: "Visceral fat is accumulating around your organs, releasing inflammatory compounds 24/7"
    },
    {
      signal: "You're tired by 3pm despite 7+ hours of sleep",
      meaning: "Your cells are struggling to efficiently convert glucose to energy—early insulin resistance"
    },
    {
      signal: "You need coffee to function and alcohol to unwind",
      meaning: "Your stress-recovery cycle is dysregulated, keeping cortisol chronically elevated"
    },
    {
      signal: "Your father or brother developed diabetes or heart disease",
      meaning: "You carry genetic susceptibility that lifestyle can either activate or suppress"
    },
    {
      signal: "Exercise doesn't produce results like it used to",
      meaning: "Your metabolic rate has declined due to muscle loss and hormonal shifts"
    }
  ];

  const consequences = [
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Cardiovascular Disease",
      stat: "2-4x",
      description: "Men with metabolic syndrome have 2-4x higher risk of heart attack and stroke. It's the #1 killer of men over 45."
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "Cognitive Decline",
      stat: "50%",
      description: "Insulin resistance in midlife increases dementia risk by 50%. Your brain runs on glucose—when that system fails, cognition suffers."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Testosterone Decline",
      stat: "30%",
      description: "Visceral fat converts testosterone to estrogen. Men with metabolic syndrome have 30% lower testosterone on average."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Shortened Lifespan",
      stat: "6-14 yrs",
      description: "Metabolic syndrome reduces life expectancy by 6-14 years. More importantly, it steals quality years decades earlier."
    }
  ];

  return (
    <div className="min-h-screen bg-[#E8E4DC]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <FigIcon className="w-6 h-6 text-[#2F3E30]" />
            <span className="font-bold text-lg" style={{ fontFamily: 'Libre Baskerville, serif' }}>FIG HEALTH</span>
          </Link>
          <Link to="/" className="text-[#525252] hover:text-[#1A1C19] text-sm flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#D1D1CD] bg-white mb-6">
              <AlertTriangle className="w-4 h-4 text-[#854D0E]" />
              <span className="text-sm text-[#525252]">For men 30-65</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>
              THE SILENT CRISIS<br />
              <span className="text-[#2F3E30]">MEN DON'T TALK ABOUT</span>
            </h1>
            <p className="text-lg text-[#525252] max-w-2xl mx-auto">
              By 45, most men are on a metabolic trajectory toward disease. 
              The problem? It's invisible until it's not. No symptoms. No warnings. 
              Just a slow accumulation of risk that explodes into crisis.
            </p>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-16 px-6 bg-[#EBEBE6]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>
            WHAT'S ACTUALLY HAPPENING
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white border border-[#D1D1CD] p-6">
              <h3 className="font-bold text-xl mb-4 text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                VISCERAL FAT
              </h3>
              <p className="text-[#525252] mb-4">
                Unlike subcutaneous fat (the pinchable kind), visceral fat wraps around your liver, pancreas, and intestines. 
                It's metabolically active—essentially an organ that secretes inflammatory chemicals, disrupts hormones, and 
                poisons your liver.
              </p>
              <p className="text-[#525252]">
                <strong className="text-[#1A1C19]">The indicator:</strong> Waist circumference over 94cm (37") signals elevated visceral fat. 
                Over 102cm (40") indicates high risk regardless of BMI.
              </p>
            </div>
            <div className="bg-white border border-[#D1D1CD] p-6">
              <h3 className="font-bold text-xl mb-4 text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                INSULIN RESISTANCE
              </h3>
              <p className="text-[#525252] mb-4">
                Your cells become deaf to insulin's signal to absorb glucose. Your pancreas compensates by producing more insulin. 
                Blood sugar stays "normal" but insulin is sky-high—this is pre-diabetes, and it can last 10-15 years before 
                showing on standard tests.
              </p>
              <p className="text-[#525252]">
                <strong className="text-[#1A1C19]">The trajectory:</strong> By the time fasting glucose is elevated, 
                you've likely lost 50% of pancreatic beta cell function. The damage started years earlier.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Silent Signals */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-4 text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>
            SILENT SIGNALS YOU'RE IGNORING
          </h2>
          <p className="text-[#525252] mb-8">
            These aren't "just part of getting older." They're early warnings.
          </p>
          <div className="space-y-4">
            {silentSignals.map((item, index) => (
              <div key={index} className="bg-white border border-[#D1D1CD] p-6 flex gap-4">
                <div className="w-8 h-8 bg-[#854D0E]/10 flex items-center justify-center flex-shrink-0 text-[#854D0E] font-bold">
                  {index + 1}
                </div>
                <div>
                  <p className="font-medium text-[#1A1C19] mb-1">{item.signal}</p>
                  <p className="text-sm text-[#525252]">{item.meaning}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consequences */}
      <section className="py-16 px-6 bg-[#2F3E30] text-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Libre Baskerville, serif' }}>
            WHERE THIS TRAJECTORY LEADS
          </h2>
          <p className="text-white/70 mb-12">
            Metabolic dysfunction doesn't announce itself. It compounds silently, then strikes.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {consequences.map((item, index) => (
              <div key={index} className="bg-white/10 border border-white/20 p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="text-[#E8E4DC]">{item.icon}</div>
                  <h3 className="font-bold text-lg" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                    {item.title.toUpperCase()}
                  </h3>
                </div>
                <p className="text-3xl font-bold text-[#E8E4DC] mb-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>
                  {item.stat}
                </p>
                <p className="text-white/70 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Good News */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-4 text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>
            THE GOOD NEWS: IT'S REVERSIBLE
          </h2>
          <p className="text-[#525252] mb-8">
            Unlike many health conditions, metabolic dysfunction responds dramatically to lifestyle intervention—
            often more effectively than medication. But you need to act before the damage becomes permanent.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6 text-center">
              <p className="text-4xl font-bold text-[#2F3E30] mb-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>58%</p>
              <p className="text-sm text-[#525252]">Diabetes risk reduction with lifestyle changes vs. 31% with medication (DPP Study)</p>
            </div>
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6 text-center">
              <p className="text-4xl font-bold text-[#2F3E30] mb-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>2 weeks</p>
              <p className="text-sm text-[#525252]">Time to see measurable improvements in insulin sensitivity with targeted changes</p>
            </div>
            <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6 text-center">
              <p className="text-4xl font-bold text-[#2F3E30] mb-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>1 inch</p>
              <p className="text-sm text-[#525252]">Waist reduction correlates with 5-10% improvement in metabolic markers</p>
            </div>
          </div>

          <div className="bg-white border border-[#D1D1CD] p-8">
            <h3 className="font-bold text-xl mb-4 text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>
              THE 90-DAY WINDOW
            </h3>
            <p className="text-[#525252] mb-6">
              90 days is the minimum effective dose for metabolic change. It's how long it takes to:
            </p>
            <ul className="space-y-3">
              {[
                "Rebuild insulin sensitivity at the cellular level",
                "Reduce visceral fat stores by 10-20%",
                "Establish new habits that override old patterns",
                "See meaningful changes in waist circumference and energy",
                "Create momentum that compounds over years"
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#3F6212] flex-shrink-0 mt-0.5" />
                  <span className="text-[#525252]">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#EBEBE6]">
        <div className="max-w-2xl mx-auto text-center">
          <Target className="w-12 h-12 text-[#2F3E30] mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4 text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>
            KNOW YOUR TRAJECTORY
          </h2>
          <p className="text-[#525252] mb-8">
            Take the free assessment. Understand where you stand. 
            Get a personalized plan to course-correct before it's too late.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/questionnaire')}
            className="bg-[#2F3E30] hover:bg-[#3A4D3B] text-white text-lg px-8 uppercase tracking-widest"
          >
            Start Free Assessment
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-sm text-[#525252] mt-4">
            Takes 10-12 minutes. No account required.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#D1D1CD]">
        <div className="max-w-4xl mx-auto text-center text-sm text-[#525252]">
          <p>
            © {new Date().getFullYear()} Fig Health. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default WhyMetabolicHealthPage;
